import React from 'react';
import { X, AlertTriangle, ArrowRight, Ban, RefreshCw } from 'lucide-react';
import type { ProcurementItemLookup } from '../../../../types/procurement';

interface ReplenishmentModalProps {
  lookup: ProcurementItemLookup;
  onKeepOriginal: () => void;
  onCancelLine: () => void;
  onSwitchToReplenishment: () => void;
  onClose: () => void;
}

export const ReplenishmentModal: React.FC<ReplenishmentModalProps> = ({
  lookup,
  onKeepOriginal,
  onCancelLine,
  onSwitchToReplenishment,
  onClose,
}) => {
  const formatDate = (iso?: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('nb-NO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-orange-500" size={22} />
            <h3 className="text-lg font-semibold text-gray-800">Lav saldo på sentrallager</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Lukk"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm text-orange-900">
            Sentrallager har ikke nok av{' '}
            <strong>
              {lookup.procurementItemNo}
              {lookup.procurementItemDescription
                ? ` – ${lookup.procurementItemDescription}`
                : ''}
            </strong>{' '}
            til å dekke anbefalt antall.
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Metric label="Ønsket antall" value={formatNumber(lookup.recommendedQuantity)} unit={lookup.recommendedUnit} />
            <Metric
              label="Saldo sentrallager"
              value={formatNumber(lookup.warehouseBalance)}
              unit={lookup.recommendedUnit}
              highlight="bad"
            />
            <Metric
              label="Forventet ankomstdato"
              value={formatDate(lookup.expectedReceptionDate)}
              fullWidth
            />
          </div>

          {lookup.hasReplenishment && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold">
                Anbefalt erstatningsvare
              </div>
              <div className="text-sm text-gray-800">
                <strong>{lookup.replenishmentItemNo}</strong>
                {lookup.replenishmentDescription ? ` – ${lookup.replenishmentDescription}` : ''}
              </div>
              <div className="text-sm text-gray-700">
                Antall: <strong>{formatNumber(lookup.recommendedQuantityReplenishmentItem)}</strong>
                {lookup.recommendedUnitReplenishmentItem
                  ? ` ${lookup.recommendedUnitReplenishmentItem}`
                  : ''}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50 grid grid-cols-1 gap-2">
          <button
            onClick={onKeepOriginal}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-lg flex items-center justify-center gap-2"
          >
            <ArrowRight size={18} />
            Behold opprinnelig vare og antall
          </button>
          {lookup.hasReplenishment && (
            <button
              onClick={onSwitchToReplenishment}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2.5 rounded-lg flex items-center justify-center gap-2"
            >
              <RefreshCw size={18} />
              Bytt til erstatningsvare
            </button>
          )}
          <button
            onClick={onCancelLine}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-4 py-2.5 rounded-lg flex items-center justify-center gap-2"
          >
            <Ban size={18} />
            Avbryt denne linjen
          </button>
        </div>
      </div>
    </div>
  );
};

interface MetricProps {
  label: string;
  value: string;
  unit?: string | null;
  highlight?: 'bad';
  fullWidth?: boolean;
}

const Metric: React.FC<MetricProps> = ({ label, value, unit, highlight, fullWidth }) => (
  <div className={`${fullWidth ? 'col-span-2' : ''} p-3 rounded border ${highlight === 'bad' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
    <div className="text-xs text-gray-600">{label}</div>
    <div className={`text-base font-semibold ${highlight === 'bad' ? 'text-red-700' : 'text-gray-800'}`}>
      {value}
      {unit ? <span className="text-sm font-normal ml-1">{unit}</span> : null}
    </div>
  </div>
);

const formatNumber = (n?: number | null) => {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('nb-NO', { maximumFractionDigits: 3 });
};
